import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { EDITOR_ROLES } from "@shared/core/types";
import type { CvAnalysis, LotReport } from "@shared/modules/x-extraction";
import { useAuthStore } from "../../../core/stores/auth";
import { useToastStore } from "../../../core/stores/toast";
import { PageHeader } from "../../../core/components/ui/page-header";
import { Button } from "../../../core/components/ui/button";
import { Card } from "../../../core/components/ui/card";
import { Badge } from "../../../core/components/ui/badge";
import { Field, Input } from "../../../core/components/ui/input";
import { Icon } from "../../../core/components/ui/icon";
import { allowedFor } from "../../../core/modules";
import { moduleEnabled } from "../../../core/blueprint";
import { attachToLot, getLotReport, importCvs, lotExportUrl, requeueJob, updateLot } from "../lib/api";
import { formatMonths, lotStats, matches, type Tally } from "../lib/profile";
import { AnalysisTable } from "../components/analysis-table";
import { ProfileModal } from "../components/profile-modal";

const ACCEPT = ".pdf,.docx,.jpg,.jpeg,.png,.webp,.tif,.tiff,.heic,application/pdf,image/*";
/** L'importation de x-documents accepte 20 fichiers par envoi ; on reste en dessous. */
const CHUNK = 10;
const MAX_FILES = 50;

function remaining(items: CvAnalysis[]): string | null {
  const pending = items.filter((a) => a.status === "en_attente" || a.status === "en_cours").length;
  const durations = items.map((a) => a.durationMs).filter((d): d is number => !!d);
  if (!pending || !durations.length) return null;
  // Un CV à la fois sur le serveur : le reste est linéaire.
  const minutes = Math.ceil(((durations.reduce((a, b) => a + b, 0) / durations.length) * pending) / 60_000);
  return minutes < 60 ? `environ ${minutes} min restante${minutes > 1 ? "s" : ""}` : `environ ${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, "0")} restantes`;
}

function TallyList({ title, rows, onPick }: { title: string; rows: Tally; onPick?: (label: string) => void }) {
  return (
    <section className="lot-stats__block">
      <h4 className="lot-stats__title">{title}</h4>
      {rows.length ? (
        <ul className="lot-stats__tags">
          {rows.map(([label, n]) => (
            <li key={label}>
              {onPick ? (
                <button type="button" className="lot-stats__tag" onClick={() => onPick(label)} title="Filtrer le rapport">
                  {label} <span className="lot-stats__count">{n}</span>
                </button>
              ) : (
                <span className="lot-stats__tag">
                  {label} <span className="lot-stats__count">{n}</span>
                </span>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">—</p>
      )}
    </section>
  );
}

export function LotPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const push = useToastStore((s) => s.push);
  const canEdit = allowedFor({ roles: EDITOR_ROLES }, user?.role) && moduleEnabled("x-documents");
  const [report, setReport] = useState<LotReport | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);
  const [sending, setSending] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState<CvAnalysis | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      setReport(await getLotReport(id));
    } catch {
      push("danger", "Lot introuvable");
      navigate("/analyse");
    }
  }, [id, navigate, push]);

  const items = report?.items ?? [];
  const running = items.some((a) => a.status === "en_attente" || a.status === "en_cours");

  useEffect(() => {
    void load();
  }, [load]);

  // Rafraîchit tant que des CV sont en lecture (un à la fois sur le serveur).
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => void load(), 8_000);
    return () => clearInterval(t);
  }, [running, load]);

  const stats = useMemo(() => lotStats(items), [items]);
  const shown = useMemo(() => items.filter((a) => matches(a, filter)), [items, filter]);

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    const next = [...files];
    for (const f of Array.from(list)) if (!next.some((g) => g.name === f.name && g.size === f.size)) next.push(f);
    if (next.length > MAX_FILES) push("warning", `${MAX_FILES} fichiers au plus par envoi : les suivants sont ignorés`);
    setFiles(next.slice(0, MAX_FILES));
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const send = async () => {
    if (!files.length) return;
    const ids: string[] = [];
    const rejected: string[] = [];
    try {
      for (let i = 0; i < files.length; i += CHUNK) {
        setSending(`Envoi ${Math.min(i + CHUNK, files.length)} / ${files.length}…`);
        const r = await importCvs(files.slice(i, i + CHUNK));
        ids.push(...r.created.map((d) => d.id));
        rejected.push(...r.rejected.map((x) => x.name));
      }
      if (ids.length) await attachToLot(id, ids);
      if (ids.length) push("success", `${ids.length} CV ajouté${ids.length > 1 ? "s" : ""} au lot : la lecture commence`);
      if (rejected.length) push("warning", `Format non accepté : ${rejected.join(", ")}`);
      setFiles([]);
    } catch {
      push("danger", ids.length ? "Envoi interrompu : une partie des CV a été ajoutée" : "Envoi impossible");
      if (ids.length) await attachToLot(id, ids).catch(() => undefined);
    } finally {
      setSending(null);
      await load();
    }
  };

  if (!report) return <div className="page">Chargement…</div>;
  const { lot } = report;
  const done = items.filter((a) => a.status !== "en_attente" && a.status !== "en_cours").length;
  const failed = items.filter((a) => a.status === "echec").length;
  const eta = remaining(items);

  return (
    <div className="page">
      <PageHeader
        title={lot.name}
        subtitle={lot.description ?? `${lot.total} CV dans ce lot`}
        actions={
          <>
            <Link className="btn btn--ghost" to="/analyse">
              Tous les lots
            </Link>
            {stats.analysed ? (
              <a className="btn btn--primary" href={lotExportUrl(lot.id)} download>
                <span className="btn__icon">
                  <Icon name="file" size={16} />
                </span>
                Exporter (Excel)
              </a>
            ) : null}
          </>
        }
      />

      <div className="lot-top">
        {canEdit && !lot.archivedAt ? (
          <Card title="Ajouter des CV" subtitle="PDF, Word (.docx), numérisations ou photos (JPEG, PNG). Tout est lu sur le serveur.">
            <div
              className={dragging ? "depot depot--actif" : "depot"}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
            >
              <Icon name="file" size={28} />
              <p className="depot__titre">Glissez les CV ici</p>
              <p className="depot__aide">ou</p>
              <Button variant="ghost" size="sm" onClick={() => inputRef.current?.click()}>
                Choisir des fichiers
              </Button>
              <input
                ref={inputRef}
                className="depot__input"
                type="file"
                multiple
                accept={ACCEPT}
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = "";
                }}
              />
            </div>
            {files.length ? (
              <ul className="depot__liste">
                {files.map((f) => (
                  <li key={`${f.name}-${f.size}`} className="depot__fichier">
                    <span className="depot__nom">{f.name}</span>
                    <span className="muted">{Math.max(1, Math.round(f.size / 1024))} Ko</span>
                    <Button variant="ghost" size="sm" aria-label={`Retirer ${f.name}`} disabled={!!sending} onClick={() => setFiles(files.filter((g) => g !== f))}>
                      <Icon name="close" size={14} />
                    </Button>
                  </li>
                ))}
              </ul>
            ) : null}
            <div className="depot__actions">
              {sending ? <span className="muted">{sending}</span> : null}
              <Button variant="primary" disabled={!files.length || !!sending} onClick={send} icon={<Icon name="sparkles" size={16} />}>
                Lancer l'analyse{files.length ? ` (${files.length})` : ""}
              </Button>
            </div>
          </Card>
        ) : null}

        <Card title="Avancement" subtitle="Un CV à la fois : l'OCR et le modèle local partagent le processeur du serveur.">
          {items.length ? (
            <div className="progression">
              <progress className="progression__barre" value={done} max={items.length} />
              <p className="progression__texte">
                <strong>
                  {done} / {items.length}
                </strong>{" "}
                CV traités{eta ? ` · ${eta}` : ""}
                {failed ? (
                  <>
                    {" "}
                    · <Badge variant="danger">{failed} en échec</Badge>
                  </>
                ) : null}
              </p>
              {running ? <p className="muted">La page se met à jour toute seule ; vous pouvez la quitter, la lecture continue.</p> : null}
            </div>
          ) : (
            <p className="muted">Aucun CV pour l'instant.</p>
          )}
        </Card>
      </div>

      {stats.analysed ? (
        <Card title="Vue d'ensemble" subtitle={`${stats.analysed} profil${stats.analysed > 1 ? "s" : ""} analysé${stats.analysed > 1 ? "s" : ""}. Cliquez une compétence pour filtrer le rapport.`}>
          <div className="lot-stats">
            <section className="lot-stats__block">
              <h4 className="lot-stats__title">Expérience</h4>
              <p className="lot-stats__figure">{formatMonths(stats.averageMonths)}</p>
              <p className="muted">en moyenne · médiane {formatMonths(stats.medianMonths)}</p>
            </section>
            <section className="lot-stats__block">
              <h4 className="lot-stats__title">Plus haut diplôme</h4>
              <ul className="lot-stats__bars">
                {stats.levels.map(([label, n]) => (
                  <li key={label} className="lot-stats__bar">
                    <span>{label}</span>
                    <progress value={n} max={stats.analysed} aria-label={`${label} : ${n}`} />
                    <span className="lot-stats__count">{n}</span>
                  </li>
                ))}
              </ul>
            </section>
            <TallyList title="Postes" rows={stats.titles} onPick={setFilter} />
            <TallyList title="Langues" rows={stats.languages} onPick={setFilter} />
            <div className="lot-stats__wide">
              <TallyList title="Compétences les plus fréquentes" rows={stats.skills} onPick={setFilter} />
            </div>
          </div>
        </Card>
      ) : null}

      <section className="lot-report">
        <div className="lot-report__bar">
          <h2 className="lot-report__title">Rapport</h2>
          <Field label="Filtrer le rapport">
            <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="ex. soudage TIG, anglais, DEC" />
          </Field>
          {filter ? (
            <Button variant="ghost" size="sm" onClick={() => setFilter("")}>
              Effacer ({shown.length} / {items.length})
            </Button>
          ) : null}
        </div>
        <AnalysisTable
          items={shown}
          sortable
          onSelect={setSelected}
          empty={filter ? "Aucun CV ne correspond au filtre." : "Aucun CV dans ce lot."}
          rowActions={
            canEdit
              ? (a) =>
                  a.status === "echec" ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={async () => {
                        await requeueJob(a.jobId);
                        await load();
                      }}
                    >
                      Relancer
                    </Button>
                  ) : null
              : undefined
          }
        />
      </section>

      {canEdit ? (
        <div className="lots__footer">
          <Button
            variant="ghost"
            size="sm"
            onClick={async () => {
              await updateLot(lot.id, { archived: !lot.archivedAt });
              push("info", lot.archivedAt ? "Lot réactivé" : "Lot archivé : ses CV restent dans le vivier");
              await load();
            }}
          >
            {lot.archivedAt ? "Réactiver le lot" : "Archiver le lot"}
          </Button>
        </div>
      ) : null}

      <ProfileModal analysis={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
